import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardHeader from '../components/DashboardHeader';
import ConfirmModal from '../components/ConfirmModal';
import TermsModal from '../components/TermsModal';
import PaymentCountdown from '../components/PaymentCountdown';
import '../styles/pageAnimation.css';
import './OrdersPage.css';

function OrdersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const ordersPerPage = 2;

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
  };

  const scaleIn = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/orders`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!res.ok) throw new Error('Failed to fetch orders');
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      console.error(err);
      alert('Could not load your orders.');
    }
  };

  // If navigated with query ?open=<orderId>, auto-open that order when orders load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    if (!openId || orders.length === 0) return;
    const match = orders.find(o => o._id === openId);
    if (match) {
      setSelectedOrder(match);
    }
  }, [location.search, orders]);

  const cancelOrder = async (orderId) => {
    // Confirm cancellation
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    // Optional message to admin (reason for cancellation)
    let reason = '';
    try {
      reason = window.prompt('Optional: Tell us why you are cancelling this order (this will be sent to the store):', '');
      if (reason === null) reason = '';
    } catch (e) { reason = ''; }

    try {
      setCancellingOrder(orderId);
      const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: 'Cancelled', message: reason }),
      });

      if (!res.ok) throw new Error('Failed to cancel order');
      
      setShowSuccessModal(true);
      await fetchOrders(); // Refresh orders
      setSelectedOrder(null); // Close modal if open
    } catch (err) {
      console.error(err);
      setErrorMessage('Could not cancel order. Please try again.');
      setShowErrorModal(true);
    } finally {
      setCancellingOrder(null);
    }
  };

  // Initial fetch on route change
  useEffect(() => {
    fetchOrders();
    // Auto-refresh shortly after navigation (gives webhook time to update)
    const timer = setTimeout(() => {
      fetchOrders();
    }, 1500);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // Polling for "Awaiting Payment" orders; cleans up on every orders change
  useEffect(() => {
    let attempts = 0;
    const awaiting = orders.filter(o => o.status === 'Awaiting Payment' && o.paymentStatus === 'pending');
    if (awaiting.length === 0) return; // nothing to poll

    const pollInterval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/payments/sync-payment-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.updated) {
          await fetchOrders();
          clearInterval(pollInterval);
        } else if (attempts >= 12) { // ~1 minute max
          clearInterval(pollInterval);
        }
      } catch (e) {
        clearInterval(pollInterval);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [orders]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">
      
      {/* Main Content */}
      <div style={{ 
        width: '100%', 
        maxWidth: isMobile ? '100%' : '1200px', 
        margin: '0 auto', 
        padding: isMobile ? '40px 20px 80px' : '60px 40px 100px'
      }}>
        
        {/* Hero Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ 
            marginBottom: isMobile ? '32px' : '48px'
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: isMobile ? '14px' : '20px',
              flexWrap: isMobile ? 'wrap' : 'nowrap'
            }}
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 100 }}
              style={{
                width: isMobile ? '56px' : '64px',
                height: isMobile ? '56px' : '64px',
                background: 'linear-gradient(135deg, #175c36 0%, #1a6b3f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(23, 92, 54, 0.20)',
                border: '3px solid rgba(255, 255, 255, 0.9)'
              }}>
              <svg width={isMobile ? "28" : "34"} height={isMobile ? "28" : "34"} viewBox="0 0 24 24" fill="white">
                <path d="M20,8H17V4H3V20H7V16H20V20H21V8H20M5,18V6H15V8H7V18H5M19,14H9V10H19V14Z"/>
              </svg>
            </motion.div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <motion.h1 
                {...fadeInUp}
                transition={{ delay: 0.4 }}
                style={{ 
                  fontSize: isMobile ? '1.8rem' : '2.6rem',
                  fontWeight: 800,
                  color: '#175c36',
                  margin: 0,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2
                }}>
                My Orders
              </motion.h1>
              
              <motion.p 
                {...fadeInUp}
                transition={{ delay: 0.5 }}
                style={{ 
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  color: '#166534',
                  margin: '6px 0 0',
                  lineHeight: 1.4,
                  fontWeight: 500,
                  textAlign: 'left'
                }}>
                Track and manage your order history
              </motion.p>
            </div>
          </div>
        </motion.div>
        
        {orders.length === 0 ? (
          <motion.div 
            {...scaleIn}
            transition={{ delay: 0.3 }}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: isMobile ? '64px 32px' : '80px 48px',
              textAlign: 'center',
              border: '2px solid #e2e8f0',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
            }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
              style={{
                width: isMobile ? '80px' : '100px',
                height: isMobile ? '80px' : '100px',
                margin: '0 auto 24px',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid #bbf7d0'
              }}>
              <svg width={isMobile ? "40" : "50"} height={isMobile ? "40" : "50"} viewBox="0 0 24 24" fill="#15803d">
                <path d="M20,8H17V4H3V20H7V16H20V20H21V8H20M5,18V6H15V8H7V18H5M19,14H9V10H19V14Z"/>
              </svg>
            </motion.div>
            <h3 style={{ 
              fontSize: isMobile ? '1.5rem' : '2rem', 
              fontWeight: 700, 
              color: '#1e293b', 
              marginBottom: '12px',
              letterSpacing: '-0.02em'
            }}>
              No orders yet
            </h3>
            <p style={{ 
              fontSize: isMobile ? '0.95rem' : '1.1rem', 
              color: '#64748b',
              marginBottom: '32px'
            }}>
              Start shopping to see your orders here
            </p>
            <motion.a
              href="/catalog"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              style={{
                display: 'inline-block',
                background: '#15803d',
                color: 'white',
                padding: isMobile ? '14px 32px' : '16px 40px',
                borderRadius: '12px',
                fontSize: isMobile ? '0.95rem' : '1rem',
                fontWeight: 600,
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
                cursor: 'pointer'
              }}>
              Browse Catalog
            </motion.a>
          </motion.div>
        ) : (
          <>
            <div
              style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '16px' : '20px',
                marginBottom: isMobile ? '48px' : '60px'
              }}>
              {/* Manual Payment Status Refresh removed by request */}
              {orders
                .slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage)
                .map((order, idx) => {
              // Calculate discounted subtotal
              const subtotal = order.items.reduce((sum, item) => {
                if (!item.product) return sum;
                if (item.product.discount && Number(item.product.discount) > 0) {
                  const discounted = Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100));
                  return sum + discounted * item.quantity;
                } else {
                  return sum + Number(item.product.price) * item.quantity;
                }
              }, 0);
              
              // Use stored delivery fee if available, otherwise calculate
              let fee = order.deliveryFee !== undefined ? order.deliveryFee : (() => {
                if (subtotal >= 10000) return 0;
                else if (order.deliveryDetails.region === 'Manila') return 1500;
                else return 800;
              })();
              
              let discountValue = 0;
              if (order.discountType === '%') {
                discountValue = (subtotal * order.discount) / 100;
              } else if (order.discountType === 'amount' || order.discountType === 'fixed') {
                discountValue = order.discount;
              }
              const total = subtotal - discountValue + fee;
              
              const orderDate = new Date(order.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
              
              const getStatusColor = (status) => {
                const s = (status || 'pending').toLowerCase();
                if (s === 'completed' || s === 'delivered') return { bg: '#dcfce7', border: '#bbf7d0', text: '#15803d' };
                if (s === 'ready for pickup') return { bg: '#f3e8ff', border: '#e9d5ff', text: '#7e22ce' };
                if (s === 'shipped') return { bg: '#fef3c7', border: '#fde68a', text: '#92400e' };
                if (s === 'cancelled') return { bg: '#fee2e2', border: '#fecaca', text: '#991b1b' };
                return { bg: '#dbeafe', border: '#bfdbfe', text: '#1e40af' };
              };
              
              const statusColors = getStatusColor(order.status);
              
              return (
                <motion.div 
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.6), ease: [0.4, 0, 0.2, 1] }}
                  role="article"
                  aria-label={`Order ${order._id}`}
                  style={{
                    background: 'white',
                    borderRadius: '24px',
                    border: '2px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative'
                  }}>
                  
                  {/* Card Header */}
                  <div style={{
                    padding: isMobile ? '24px' : '28px 32px',
                    background: 'linear-gradient(135deg, #f8fdf9 0%, #f0f7f1 100%)',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '16px' : '20px'
                  }}>
                    <div>
                      <div style={{
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                        fontWeight: 700,
                        color: '#64748b',
                        letterSpacing: '0.5px',
                        marginBottom: '4px'
                      }}>
                        ORDER #{String(order._id).slice(-8)}
                      </div>
                      <div style={{
                        fontSize: isMobile ? '0.8rem' : '0.85rem',
                        color: '#94a3b8',
                        fontWeight: 500
                      }}>
                        {orderDate}
                      </div>
                    </div>
                    <div style={{
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      background: statusColors.bg,
                      border: `2px solid ${statusColors.border}`,
                      borderRadius: '12px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 700,
                      color: statusColors.text,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}>
                      {order.status || 'Pending'}
                    </div>
                  </div>
                  
                  {/* Payment Countdown Timer for Awaiting Payment Orders */}
                  {order.status === 'Awaiting Payment' && order.paymentStatus === 'pending' && (
                    <div style={{ padding: isMobile ? '16px 24px 0' : '20px 32px 0' }}>
                      <PaymentCountdown 
                        orderCreatedAt={order.createdAt}
                        onExpire={() => fetchOrders()}
                      />
                    </div>
                  )}
                  
                  {/* Card Body - Products */}
                  <div style={{
                    padding: isMobile ? '20px' : '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '12px' : '16px',
                      marginBottom: isMobile ? '20px' : '24px'
                    }}>
                      {order.items.map((item, i) => {
                        if (!item.product) return null;
                        return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '14px' : '18px',
                            padding: isMobile ? '16px' : '20px',
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                          {item.product.discount && Number(item.product.discount) > 0 ? (
                            <div style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              letterSpacing: '0.3px',
                              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                            }}>
                              -{item.product.discount}%
                            </div>
                          ) : null}
                          <div 
                            onClick={() => navigate(`/product/${item.product._id}`)}
                            style={{
                              width: isMobile ? '70px' : '80px',
                              height: isMobile ? '70px' : '80px',
                              borderRadius: '14px',
                              overflow: 'hidden',
                              flexShrink: 0,
                              background: 'white',
                              border: '3px solid #e2e8f0',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                            }}
                          >
                            <img 
                              src={item.product.image} 
                              alt={item.product.name} 
                              loading="lazy"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: isMobile ? '1rem' : '1.05rem',
                              fontWeight: 700,
                              color: '#1e293b',
                              marginBottom: '6px',
                              lineHeight: 1.3,
                              letterSpacing: '-0.01em'
                            }}>
                              {item.product.name}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '0.85rem' : '0.9rem',
                              color: '#64748b',
                              display: 'flex',
                              gap: '10px',
                              alignItems: 'center',
                              flexWrap: 'wrap'
                            }}>
                              <span style={{ 
                                background: '#f1f5f9',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                color: '#475569',
                                fontSize: '0.8rem'
                              }}>Qty: {item.quantity}</span>
                              {item.product.discount && Number(item.product.discount) > 0 ? (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <span style={{ 
                                    textDecoration: 'line-through',
                                    color: '#94a3b8',
                                    fontSize: '0.8rem'
                                  }}>
                                    ₱{Number(item.product.price).toFixed(2)}
                                  </span>
                                  <span style={{ 
                                    fontWeight: 700, 
                                    color: '#dc2626',
                                    fontSize: '0.95rem'
                                  }}>
                                    ₱{(Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100))).toFixed(2)}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ fontWeight: 700, color: '#15803d', fontSize: '0.95rem' }}>
                                  ₱{Number(item.product.price).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                    
                    {/* Footer - Total and Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '16px' : '20px',
                      paddingTop: isMobile ? '16px' : '20px',
                      borderTop: '2px solid #f1f5f9'
                    }}>
                      <div>
                        <div style={{
                          fontSize: isMobile ? '0.75rem' : '0.8rem',
                          color: '#94a3b8',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Total Amount
                        </div>
                        <div style={{
                          fontSize: isMobile ? '1.5rem' : '1.75rem',
                          fontWeight: 800,
                          color: '#175c36',
                          letterSpacing: '-0.02em'
                        }}>
                          ₱{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: isMobile ? '12px' : '14px',
                        width: isMobile ? '100%' : 'auto',
                        flexDirection: isMobile ? 'column' : 'row'
                      }}>
                        <motion.button 
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedOrder(order)}
                          aria-label={`View details for order ${order._id}`}
                          style={{
                            background: '#15803d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '14px',
                            padding: isMobile ? '16px 28px' : '14px 32px',
                            fontSize: isMobile ? '0.95rem' : '1rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            whiteSpace: 'nowrap',
                            flex: isMobile ? 1 : 'auto',
                            letterSpacing: '0.3px',
                            minHeight: '48px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          View Details
                        </motion.button>
                        {(!order.status || order.status === 'Pending' || order.status === 'Awaiting Payment') && (
                          <>
                            {order.status === 'Awaiting Payment' && order.paymentLink && (
                              <motion.button 
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(order.paymentLink, '_blank');
                                }}
                                aria-label="View Payment Link"
                                style={{
                                  background: '#2e7d32',
                                  color: 'white',
                                  border: '2px solid #2e7d32',
                                  borderRadius: '14px',
                                  padding: isMobile ? '16px 28px' : '14px 32px',
                                  fontSize: isMobile ? '0.95rem' : '1rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.2)',
                                  whiteSpace: 'nowrap',
                                  flex: isMobile ? 1 : 'auto',
                                  letterSpacing: '0.3px',
                                  minHeight: '48px',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  marginRight: '12px'
                                }}
                              >
                                💳 View Payment Link
                              </motion.button>
                            )}
                            <motion.button 
                              whileHover={!cancellingOrder ? { scale: 1.02, y: -2, borderColor: '#dc2626' } : {}}
                              whileTap={!cancellingOrder ? { scale: 0.98 } : {}}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelOrder(order._id);
                              }}
                              disabled={cancellingOrder === order._id}
                              aria-label={`Cancel order ${order._id}`}
                              aria-busy={cancellingOrder === order._id}
                              style={{
                                background: cancellingOrder === order._id ? '#f8fafc' : 'white',
                                color: cancellingOrder === order._id ? '#94a3b8' : '#ef4444',
                                border: `2px solid ${cancellingOrder === order._id ? '#e2e8f0' : '#ef4444'}`,
                                borderRadius: '14px',
                                padding: isMobile ? '16px 28px' : '14px 32px',
                                fontSize: isMobile ? '0.95rem' : '1rem',
                                fontWeight: 700,
                                cursor: cancellingOrder === order._id ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                flex: isMobile ? 1 : 'auto',
                                opacity: cancellingOrder === order._id ? 0.7 : 1,
                                letterSpacing: '0.3px',
                                minHeight: '48px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                              }}
                            >
                              {cancellingOrder === order._id ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25"/>
                                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" fill="none"/>
                                  </svg>
                                  Cancelling...
                                </span>
                              ) : 'Cancel Order'}
                            </motion.button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            </div>
            
            {/* Pagination */}
            {orders.length > ordersPerPage && (
              <motion.div 
                style={{ 
                  background: '#fff', 
                  borderRadius: 16, 
                  padding: isMobile ? 16 : 20, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                  border: '1.5px solid #f0f0f0', 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  gap: isMobile ? 12 : 16, 
                  marginTop: 24 
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div style={{ color: '#6b7280', fontSize: isMobile ? '0.8rem' : '0.9rem', fontWeight: 500, textAlign: isMobile ? 'center' : 'left' }}>
                  Showing {orders.length > 0 ? ((currentPage - 1) * ordersPerPage) + 1 : 0}-{Math.min(currentPage * ordersPerPage, orders.length)} of {orders.length} orders
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? 6 : 8, flexWrap: 'wrap' }}>
                  <motion.button
                    whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                    whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: currentPage === 1 ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      color: currentPage === 1 ? '#9ca3af' : '#4a5568',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: isMobile ? 40 : 44,
                      opacity: currentPage === 1 ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    First
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                    whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: currentPage === 1 ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      color: currentPage === 1 ? '#9ca3af' : '#4a5568',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: isMobile ? 40 : 44,
                      opacity: currentPage === 1 ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  >
                    Previous
                  </motion.button>
                  {Array.from({ length: Math.ceil(orders.length / ordersPerPage) }, (_, i) => {
                    const pageNum = i + 1;
                    const totalPages = Math.ceil(orders.length / ordersPerPage);
                    if (
                      pageNum === 1 || 
                      pageNum === totalPages || 
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <motion.button
                          key={pageNum}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          style={{
                            background: currentPage === pageNum ? 'linear-gradient(135deg, #7a9c3a 0%, #88ab47 100%)' : '#fff',
                            border: currentPage === pageNum ? '2px solid #7a9c3a' : '2px solid #e5e7eb',
                            borderRadius: 10,
                            padding: isMobile ? '8px 12px' : '10px 16px',
                            fontWeight: 600,
                            fontSize: isMobile ? '0.85rem' : '1rem',
                            color: currentPage === pageNum ? '#fff' : '#4a5568',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minHeight: isMobile ? 40 : 44,
                            minWidth: isMobile ? 40 : 44,
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent',
                            boxShadow: currentPage === pageNum ? '0 2px 8px rgba(122, 156, 58, 0.2)' : 'none',
                          }}
                          onMouseOver={e => {
                            if (currentPage !== pageNum) {
                              e.currentTarget.style.borderColor = '#7a9c3a';
                              e.currentTarget.style.background = '#f7faf4';
                            }
                          }}
                          onMouseOut={e => {
                            if (currentPage !== pageNum) {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.background = '#fff';
                            }
                          }}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} style={{ color: '#9ca3af', padding: '0 8px' }}>...</span>;
                    }
                    return null;
                  })}
                  <motion.button
                    whileHover={{ scale: currentPage === Math.ceil(orders.length / ordersPerPage) ? 1 : 1.05 }}
                    whileTap={{ scale: currentPage === Math.ceil(orders.length / ordersPerPage) ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: currentPage === Math.ceil(orders.length / ordersPerPage) ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      color: currentPage === Math.ceil(orders.length / ordersPerPage) ? '#9ca3af' : '#4a5568',
                      cursor: currentPage === Math.ceil(orders.length / ordersPerPage) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: isMobile ? 40 : 44,
                      opacity: currentPage === Math.ceil(orders.length / ordersPerPage) ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={currentPage === Math.ceil(orders.length / ordersPerPage)}
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(orders.length / ordersPerPage), prev + 1))}
                  >
                    Next
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: currentPage === Math.ceil(orders.length / ordersPerPage) ? 1 : 1.05 }}
                    whileTap={{ scale: currentPage === Math.ceil(orders.length / ordersPerPage) ? 1 : 0.95 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      background: currentPage === Math.ceil(orders.length / ordersPerPage) ? '#f3f4f6' : '#fff',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.85rem' : '1rem',
                      color: currentPage === Math.ceil(orders.length / ordersPerPage) ? '#9ca3af' : '#4a5568',
                      cursor: currentPage === Math.ceil(orders.length / ordersPerPage) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      minHeight: isMobile ? 40 : 44,
                      opacity: currentPage === Math.ceil(orders.length / ordersPerPage) ? 0.4 : 1,
                      touchAction: 'manipulation',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    disabled={currentPage === Math.ceil(orders.length / ordersPerPage)}
                    onClick={() => setCurrentPage(Math.ceil(orders.length / ordersPerPage))}
                  >
                    Last
                  </motion.button>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
      
      {/* Modal for order details */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSelectedOrder(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '20px' : '40px',
              overflowY: 'auto'
            }}>
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                width: '100%',
                maxWidth: isMobile ? '100%' : '900px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
              
              {/* Modal Header */}
              <div style={{
                padding: isMobile ? '20px' : '28px 32px',
                borderBottom: '2px solid #e2e8f0',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{
                    fontSize: isMobile ? '1.25rem' : '1.5rem',
                    fontWeight: 800,
                    color: '#1e293b',
                    marginBottom: '4px',
                    letterSpacing: '-0.02em'
                  }}>
                    Order #{String(selectedOrder._id).slice(-8)}
                  </h2>
                  <p style={{
                    fontSize: isMobile ? '0.8rem' : '0.85rem',
                    color: '#64748b',
                    margin: 0
                  }}>
                    {new Date(selectedOrder.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90, background: '#e2e8f0' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedOrder(null)}
                  aria-label="Close order details modal"
                  style={{
                    background: '#f1f5f9',
                    border: '2px solid #e2e8f0',
                    borderRadius: '50%',
                    width: isMobile ? '44px' : '48px',
                    height: isMobile ? '44px' : '48px',
                    minWidth: '44px',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#64748b',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    flexShrink: 0
                  }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </motion.button>
              </div>
              
              {/* Modal Body - Scrollable */}
              <div style={{
                overflowY: 'auto',
                flex: 1,
                padding: isMobile ? '20px' : '32px'
              }}>
                {(() => {
                  // Calculate values
                  const subtotal = selectedOrder.items.reduce((sum, item) => {
                    if (!item.product) return sum;
                    if (item.product.discount && Number(item.product.discount) > 0) {
                      const discounted = Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100));
                      return sum + discounted * item.quantity;
                    } else {
                      return sum + Number(item.product.price) * item.quantity;
                    }
                  }, 0);
                  
                  let fee = selectedOrder.deliveryFee !== undefined ? selectedOrder.deliveryFee : (() => {
                    if (subtotal >= 10000) return 0;
                    else if (selectedOrder.deliveryDetails.region === 'Manila') return 1500;
                    else return 800;
                  })();
                  
                  let discountValue = 0;
                  if (selectedOrder.discountType === '%') {
                    discountValue = (subtotal * selectedOrder.discount) / 100;
                  } else if (selectedOrder.discountType === 'amount' || selectedOrder.discountType === 'fixed') {
                    discountValue = selectedOrder.discount;
                  }
                  const total = subtotal - discountValue + fee;
                  
                  const getStatusColor = (status) => {
                    const s = (status || 'pending').toLowerCase();
                    if (s === 'completed' || s === 'delivered') return { bg: '#dcfce7', border: '#bbf7d0', text: '#15803d' };
                    if (s === 'shipped') return { bg: '#fef3c7', border: '#fde68a', text: '#92400e' };
                    if (s === 'cancelled') return { bg: '#fee2e2', border: '#fecaca', text: '#991b1b' };
                    if (s === 'awaiting payment') {
                      // Check if payment expired
                      if (selectedOrder.paymentStatus === 'expired') {
                        return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
                      }
                      return { bg: '#fef3c7', border: '#fde68a', text: '#d97706' };
                    }
                    return { bg: '#dbeafe', border: '#bfdbfe', text: '#1e40af' };
                  };
                  
                  const statusColors = getStatusColor(selectedOrder.status);
                  
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '24px' : '32px'
                    }}>
                      {/* Left Column - Order Info */}
                      <div style={{ flex: isMobile ? 'none' : 1 }}>
                        {/* Status Badge */}
                        <div style={{
                          padding: '12px 20px',
                          background: statusColors.bg,
                          border: `2px solid ${statusColors.border}`,
                          borderRadius: '12px',
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          color: statusColors.text,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          marginBottom: '24px',
                          textAlign: 'center'
                        }}>
                          {selectedOrder.paymentStatus === 'expired' ? 'PAYMENT EXPIRED' : (selectedOrder.status || 'Pending')}
                        </div>

                        {/* Payment Countdown Timer for Awaiting Payment Orders */}
                        {selectedOrder.status === 'Awaiting Payment' && selectedOrder.paymentStatus === 'pending' && (
                          <PaymentCountdown 
                            orderCreatedAt={selectedOrder.createdAt}
                            onExpire={() => fetchOrders()}
                          />
                        )}

                        {/* Order Information - Short ID */}
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Order Information
                          </div>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            padding: isMobile ? '18px' : '24px',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '160px 1fr',
                              gap: '8px 12px'
                            }}>
                              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.5px' }}>Order ID</div>
                              <div style={{ fontSize: '0.95rem', color: '#1e293b', fontWeight: 700 }}>{String(selectedOrder._id).slice(-8)}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Contact Information */}
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Contact Information
                          </div>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            padding: isMobile ? '18px' : '24px',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div style={{
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              color: '#1e293b',
                              marginBottom: '6px',
                              letterSpacing: '-0.01em'
                            }}>
                              {selectedOrder.deliveryDetails.firstName} {selectedOrder.deliveryDetails.lastName}
                            </div>
                            <div style={{
                              fontSize: '0.95rem',
                              color: '#64748b',
                              lineHeight: 1.5,
                              marginBottom: '4px'
                            }}>
                              {selectedOrder.deliveryDetails.email || selectedOrder.deliveryDetails.phoneNumber || 'No contact info'}
                            </div>
                            {selectedOrder.deliveryDetails.email && selectedOrder.deliveryDetails.phoneNumber && (
                              <div style={{
                                fontSize: '0.95rem',
                                color: '#64748b',
                                lineHeight: 1.5
                              }}>
                                {selectedOrder.deliveryDetails.phoneNumber}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Payment Information */}
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Payment Method
                          </div>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            padding: isMobile ? '18px' : '24px',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div style={{
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: '#1e293b',
                              marginBottom: '8px'
                            }}>
                              {selectedOrder.paymentMethod === 'cod' ? 'Cash on Delivery' : selectedOrder.paymentMethod || 'Gcash'}
                            </div>
                            <div style={{
                              fontSize: '1.5rem',
                              fontWeight: 800,
                              color: '#175c36',
                              letterSpacing: '-0.02em'
                            }}>
                              ₱{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                        
                        {/* Shipping Address - Hidden for Store Pickup */}
                        {selectedOrder.shippingMethod !== 'pickup' && (
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Shipping Address
                          </div>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            padding: isMobile ? '18px' : '24px',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div style={{
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: '#1e293b',
                              marginBottom: '4px'
                            }}>
                              {selectedOrder.deliveryDetails.firstName} {selectedOrder.deliveryDetails.lastName}
                            </div>
                            <div style={{
                              fontSize: '0.9rem',
                              color: '#64748b',
                              lineHeight: 1.6
                            }}>
                              {selectedOrder.deliveryDetails.fullAddress}<br/>
                              {selectedOrder.deliveryDetails.city}, {selectedOrder.deliveryDetails.region}, {selectedOrder.deliveryDetails.postalCode}<br/>
                              Philippines
                            </div>
                          </div>
                        </div>
                        )}
                        
                        {/* Shipping Method */}
                        <div>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Shipping Method
                          </div>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                            padding: isMobile ? '18px' : '24px',
                            borderRadius: '16px',
                            border: '2px solid #e2e8f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: selectedOrder.shippingMethod === 'pickup' ? '#15803d' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            {selectedOrder.shippingMethod === 'pickup' ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="#15803d">
                                <path d="M12,2L4,5V11.09C4,16.14 7.41,20.85 12,22C16.59,20.85 20,16.14 20,11.09V5L12,2M18,11.09C18,15.09 15.45,18.79 12,19.92C8.55,18.79 6,15.09 6,11.09V6.39L12,4.14L18,6.39V11.09M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="#15803d">
                                <path d="M18,18.5C18.83,18.5 19.5,17.83 19.5,17C19.5,16.17 18.83,15.5 18,15.5C17.17,15.5 16.5,16.17 16.5,17C16.5,17.83 17.17,18.5 18,18.5M19.5,9.5L21.46,12H17V9.5M6,18.5C6.83,18.5 7.5,17.83 7.5,17C7.5,16.17 6.83,15.5 6,15.5C5.17,15.5 4.5,16.17 4.5,17C4.5,17.83 5.17,18.5 6,18.5M20,8H17V4H3C1.89,4 1,4.89 1,6V17H3C3,18.66 4.34,20 6,20C7.66,20 9,18.66 9,17H15C15,18.66 16.34,20 18,20C19.66,20 21,18.66 21,17H23V12L20,8Z"/>
                              </svg>
                            )}
                            <span>
                              {selectedOrder.shippingMethod === 'pickup' ? '🏪 Store Pickup' :
                               selectedOrder.shippingMethod === 'lalamove' ? '🚗 Lalamove Delivery' :
                               '🚚 Standard Shipping'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Messages */}
                      {selectedOrder.messages && selectedOrder.messages.length > 0 && (
                        <div style={{ marginTop: 20, marginBottom: 12 }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '8px'
                          }}>Messages</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {selectedOrder.messages.map((m, i) => (
                              <div key={i} style={{ background: '#fff', border: '1px solid #e6e6e6', padding: 12, borderRadius: 10 }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{m.from === 'admin' ? 'From store' : 'From you'}</div>
                                <div style={{ fontSize: '0.9rem', color: '#475569', marginTop: 6 }}>{m.message}</div>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>{new Date(m.createdAt).toLocaleString()}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Right Column - Products & Summary */}
                      <div style={{ flex: isMobile ? 'none' : 1 }}>
                        {/* Products List */}
                        <div style={{ marginBottom: '24px' }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#94a3b8',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '12px'
                          }}>
                            Order Items ({selectedOrder.items.length})
                          </div>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            {selectedOrder.items.map((item, i) => {
                              if (!item.product) return null;
                              return (
                              <div
                                key={i}
                                style={{
                                  background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
                                  padding: isMobile ? '16px' : '20px',
                                  borderRadius: '16px',
                                  border: '2px solid #e2e8f0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '16px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                  position: 'relative'
                                }}>
                                {item.product.discount && Number(item.product.discount) > 0 ? (
                                  <div style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                                    color: 'white',
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.3px',
                                    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                                  }}>
                                    -{item.product.discount}%
                                  </div>
                                ) : null}
                                <div 
                                  onClick={() => navigate(`/product/${item.product._id}`)}
                                  style={{
                                    width: isMobile ? '70px' : '80px',
                                    height: isMobile ? '70px' : '80px',
                                    borderRadius: '14px',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    background: 'white',
                                    border: '3px solid #e2e8f0',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                  }}
                                >
                                  <img
                                    src={item.product.image || '/default-plant.png'}
                                    alt={item.product.name}
                                    loading="lazy"
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'cover' 
                                    }}
                                  />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    marginBottom: '6px',
                                    lineHeight: 1.3,
                                    letterSpacing: '-0.01em'
                                  }}>
                                    {item.product.name}
                                  </div>
                                  <div style={{
                                    fontSize: '0.9rem',
                                    color: '#64748b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    <span style={{ 
                                      background: '#f1f5f9',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      fontWeight: 600,
                                      color: '#475569',
                                      fontSize: '0.8rem'
                                    }}>Qty: {item.quantity}</span>
                                  </div>
                                </div>
                                <div style={{
                                  fontSize: '1rem',
                                  fontWeight: 800,
                                  color: '#175c36',
                                  textAlign: 'right',
                                  flexShrink: 0,
                                  minWidth: '90px'
                                }}>
                                  {item.product.discount && Number(item.product.discount) > 0 ? (
                                    <div>
                                      <div style={{ 
                                        fontSize: '0.75rem', 
                                        textDecoration: 'line-through', 
                                        color: '#94a3b8',
                                        fontWeight: 500,
                                        marginBottom: '2px'
                                      }}>
                                        ₱{(Number(item.product.price) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </div>
                                      <div style={{ color: '#dc2626' }}>
                                        ₱{((Number(item.product.price) - (Number(item.product.price) * (Number(item.product.discount) / 100))) * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </div>
                                    </div>
                                  ) : (
                                    `₱${(item.product.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                  )}
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                        
                        {/* Order Summary */}
                        <div style={{
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                          padding: isMobile ? '20px' : '24px',
                          borderRadius: '16px',
                          border: '2px solid #bbf7d0'
                        }}>
                          <div style={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: '#15803d',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '16px'
                          }}>
                            Order Summary
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              fontSize: '0.9rem',
                              color: '#166534'
                            }}>
                              <span>Subtotal</span>
                              <span style={{ fontWeight: 600 }}>₱{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              fontSize: '0.9rem',
                              color: '#166534'
                            }}>
                              <span>Delivery Fee</span>
                              <span style={{ fontWeight: 600 }}>₱{fee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            
                            {discountValue > 0 && (
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                fontSize: '0.9rem',
                                color: '#15803d',
                                fontWeight: 600,
                                alignItems: 'center'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <span>Discount</span>
                                  {selectedOrder.promoCode && (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      background: '#dcfce7',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      color: '#166534',
                                      fontWeight: 700,
                                      letterSpacing: '0.5px',
                                      border: '1px solid #bbf7d0'
                                    }}>
                                      {selectedOrder.promoCode}
                                    </span>
                                  )}
                                  {selectedOrder.voucherId && !selectedOrder.promoCode && (
                                    <span style={{
                                      fontSize: '0.75rem',
                                      background: '#fef3c7',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      color: '#92400e',
                                      fontWeight: 700,
                                      letterSpacing: '0.5px',
                                      border: '1px solid #fde68a'
                                    }}>
                                      🎁 REWARD
                                    </span>
                                  )}
                                </span>
                                <span>
                                  {selectedOrder.discountType === '%'
                                    ? `-₱${discountValue.toLocaleString(undefined, { minimumFractionDigits: 2 })} (${selectedOrder.discount}%)`
                                    : `-₱${discountValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            )}
                            
                            <div style={{
                              height: '1px',
                              background: '#bbf7d0',
                              margin: '8px 0'
                            }} />
                            
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              fontSize: '1.25rem',
                              fontWeight: 800,
                              color: '#15803d',
                              letterSpacing: '-0.02em'
                            }}>
                              <span>Total</span>
                              <span>₱{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: isMobile ? '12px' : '14px',
                          marginTop: '28px',
                          flexDirection: isMobile ? 'column' : 'row'
                        }}>
                          {selectedOrder.status === 'Awaiting Payment' && selectedOrder.paymentStatus !== 'expired' && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={async () => {
                                try {
                                  // Check if order already has a payment link
                                  if (selectedOrder.paymentLink) {
                                    // Reuse existing payment link
                                    window.location.href = selectedOrder.paymentLink;
                                    return;
                                  }

                                  // Only create new payment link if one doesn't exist
                                  const response = await axios.post(
                                    `${process.env.REACT_APP_API}/api/payments/create-payment-link`,
                                    {
                                      amount: Math.round(total * 100),
                                      deliveryDetails: selectedOrder.deliveryDetails,
                                      discount: selectedOrder.discount || 0,
                                      discountType: selectedOrder.discountType || '',
                                      promoCode: selectedOrder.promoCode || '',
                                      voucherId: selectedOrder.voucherId || null,
                                      deliveryFee: fee,
                                      shippingMethod: selectedOrder.shippingMethod || 'standard',
                                      orderId: selectedOrder._id
                                    },
                                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                  );
                                  if (response.data.checkoutUrl) {
                                    window.location.href = response.data.checkoutUrl;
                                  }
                                } catch (error) {
                                  console.error('Payment error:', error);
                                  alert('Failed to create payment link. Please try again.');
                                }
                              }}
                              style={{
                                flex: 1,
                                background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '14px',
                                padding: isMobile ? '16px 28px' : '14px 32px',
                                fontSize: '1rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(21, 128, 61, 0.3)',
                                letterSpacing: '0.3px',
                                minHeight: '48px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                              }}
                            >
                              💳 Pay Now
                            </motion.button>
                          )}
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setSelectedOrder(null)}
                            aria-label="Close order details"
                            style={{
                              flex: 1,
                              background: '#15803d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '14px',
                              padding: isMobile ? '16px 28px' : '14px 32px',
                              fontSize: '1rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              letterSpacing: '0.3px',
                              minHeight: '48px',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                          >
                            Close
                          </motion.button>
                          {(!selectedOrder.status || selectedOrder.status === 'Pending' || selectedOrder.status === 'Awaiting Payment') && (
                            <motion.button
                              whileHover={!cancellingOrder ? { scale: 1.02, borderColor: '#dc2626' } : {}}
                              whileTap={!cancellingOrder ? { scale: 0.98 } : {}}
                              onClick={() => cancelOrder(selectedOrder._id)}
                              disabled={cancellingOrder === selectedOrder._id}
                              aria-label="Cancel this order"
                              aria-busy={cancellingOrder === selectedOrder._id}
                              style={{
                                flex: 1,
                                background: cancellingOrder === selectedOrder._id ? '#f8fafc' : 'white',
                                color: cancellingOrder === selectedOrder._id ? '#94a3b8' : '#ef4444',
                                border: `2px solid ${cancellingOrder === selectedOrder._id ? '#e2e8f0' : '#ef4444'}`,
                                borderRadius: '14px',
                                padding: isMobile ? '16px 28px' : '14px 32px',
                                fontSize: '1rem',
                                fontWeight: 700,
                                cursor: cancellingOrder === selectedOrder._id ? 'not-allowed' : 'pointer',
                                opacity: cancellingOrder === selectedOrder._id ? 0.7 : 1,
                                letterSpacing: '0.3px',
                                minHeight: '48px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                              }}
                            >
                              {cancellingOrder === selectedOrder._id ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25"/>
                                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" fill="none"/>
                                  </svg>
                                  Cancelling...
                                </span>
                              ) : 'Cancel Order'}
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer style={{
        width: '100%',
        background: 'linear-gradient(135deg, #0f4527 0%, #145a34 50%, #0f4527 100%)',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="viber://chat?number=639368983053"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Viber"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
                <a
                  href="https://www.messenger.com/t/61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      </div>
      
      <style>{`
        @media (max-width: 768px) {
          .orders-page-title {
            font-size: 1.5rem !important;
            margin-bottom: 1.5rem !important;
          }
          .order-card {
            width: 100% !important;
            max-width: 400px !important;
            padding: 1.5rem !important;
          }
          .order-card h2 {
            font-size: 1.5rem !important;
          }
          .order-image-box {
            width: 160px !important;
            height: 160px !important;
          }
          .order-image-box img {
            max-width: 140px !important;
            max-height: 140px !important;
          }
        }
        @media (max-width: 480px) {
          .orders-page-title {
            font-size: 1.3rem !important;
            margin-bottom: 1.25rem !important;
          }
          .order-card {
            padding: 1.25rem !important;
          }
          .order-card h2 {
            font-size: 1.3rem !important;
            margin-bottom: 0.75rem !important;
          }
          .order-status-badge {
            font-size: 0.9rem !important;
            padding: 0.25rem 0.75rem !important;
            min-width: 100px !important;
          }
          .order-image-box {
            width: 140px !important;
            height: 140px !important;
          }
          .order-image-box img {
            max-width: 120px !important;
            max-height: 120px !important;
          }
          .order-item-name {
            font-size: 1rem !important;
          }
          .modal-overlay {
            padding: 10px !important;
          }
          .modal-content {
            padding: 1.25rem !important;
            max-width: 95% !important;
          }
          .modal-content h2 {
            font-size: 1.3rem !important;
          }
        }
      `}</style>

      <ConfirmModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onConfirm={() => {}}
        title="Order Cancelled"
        message="Your order has been cancelled successfully"
        confirmText="OK"
        cancelText=""
        type="success"
      />

      <ConfirmModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
        onConfirm={() => {}}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="error"
      />

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />
    </div>
  );
}

export default OrdersPage;
